import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";
import {
  Sparkles,
  Search,
  Bot,
  Compass,
  TrendingUp,
  Target,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
} from "lucide-react";

const PAGE_TITLE =
  "AI Visibility Nedir? Yapay Zekâlarda Görünür Olmanın Yeni Yolu | uSeen";
const PAGE_DESC =
  "AI Visibility, markanızın ChatGPT, Gemini, Google AI ve diğer yapay zekâ destekli arama sistemlerinde ne kadar görünür ve önerilebilir olduğunu gösterir. uSeen ile analiz edin, eksikleri görün, görünürlüğünüzü artırın.";

const FAQ_ITEMS = [
  {
    q: "AI Visibility nedir?",
    a: "AI Visibility, bir işletmenin veya markanın yapay zekâ destekli arama ve cevap sistemlerinde ne kadar görünür, anlaşılır ve önerilebilir olduğunu gösteren dijital görünürlük alanıdır.",
  },
  {
    q: "AI Visibility SEO'nun yerine mi geçer?",
    a: "Hayır. SEO hâlâ önemlidir. AI Visibility, SEO'nun üzerine eklenen yeni bir görünürlük katmanıdır. Amaç sadece Google'da sıralanmak değil, yapay zekâ cevaplarında da önerilebilir olmaktır.",
  },
  {
    q: "İşletmem ChatGPT'de nasıl önerilir?",
    a: "Bunun için işletme bilgilerinizin internette doğru, tutarlı ve güvenilir olması gerekir. Web siteniz, Google Business Profile hesabınız, müşteri yorumlarınız, sosyal medya hesaplarınız ve üçüncü parti kaynaklardaki bilgileriniz bir bütün olarak değerlendirilir.",
  },
  {
    q: "uSeen ne yapar?",
    a: "uSeen, işletmenizin yapay zekâlarda ne kadar görünür olduğunu analiz eder, rakiplerinizle karşılaştırır, eksikleri belirler ve görünürlüğünüzü artıracak görevler oluşturur.",
  },
  {
    q: "AI Visibility kimler için önemlidir?",
    a: "Özellikle restoran, klinik, güzellik merkezi, otel, kamp alanı, oto servis, emlak ofisi ve yerel hizmet işletmeleri için önemlidir. Ancak dijitalde bulunmak isteyen her marka için giderek daha kritik hale gelmektedir.",
  },
];

/**
 * Public SEO landing page — /ai-visibility-nedir
 * No auth required. Marketing/guide tone. FAQPage JSON-LD embedded for rich result eligibility.
 */
export default function AIVisibilityNedir() {
  // Sets <title>, meta description, OG/Twitter tags + injects FAQPage JSON-LD.
  // Restores original values on unmount so other routes keep their generic uSeen branding.
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = PAGE_TITLE;
    if (metaDesc) metaDesc.setAttribute("content", PAGE_DESC);

    const tags = [
      ["property", "og:title", PAGE_TITLE],
      ["property", "og:description", PAGE_DESC],
      ["property", "og:type", "article"],
      ["name", "twitter:title", PAGE_TITLE],
      ["name", "twitter:description", PAGE_DESC],
    ];
    const added = [];
    for (const [attr, key, val] of tags) {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
        added.push(tag);
      }
      tag.setAttribute("content", val);
    }

    // FAQPage structured data
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-useen-faq", "ai-visibility");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== undefined && prevDesc !== null) {
        metaDesc.setAttribute("content", prevDesc);
      }
      added.forEach((t) => t.parentNode?.removeChild(t));
      ld.parentNode?.removeChild(ld);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-slate-200 flex flex-col" data-testid="ai-visibility-page">
      <div className="absolute inset-0 bg-grid opacity-[0.08] pointer-events-none" />

      <header className="relative z-10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center"><Logo /></Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/fiyatlandirma" className="text-slate-300 hover:text-cyan-300">Fiyatlandırma</Link>
          <Link to="/giris" className="text-slate-300 hover:text-cyan-300">Giriş Yap</Link>
          <Link to="/kayit" className="neon-btn rounded-lg px-4 py-2 text-white text-sm font-medium" data-testid="header-cta-register">
            Ücretsiz Başla
          </Link>
        </nav>
      </header>

      <main className="relative z-10 flex-1 px-6 lg:px-12 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300 mb-3 inline-flex items-center gap-1.5">
            <Sparkles size={11} /> AI Visibility · Rehber
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
            AI Visibility Nedir?
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-3">
            AI Visibility, bir markanın, işletmenin veya web sitesinin
            <strong className="text-white"> ChatGPT, Gemini, Google AI, Perplexity </strong>
            ve benzeri yapay zekâ destekli arama sistemlerinde
            <strong className="text-white"> ne kadar görünür, anlaşılır ve önerilebilir </strong>
            olduğunu ifade eder.
          </p>
          <p className="text-slate-400 leading-relaxed mb-6">
            Bugüne kadar işletmeler Google'da üst sıralara çıkmak için SEO çalışmaları yapıyordu.
            Ancak artık kullanıcılar sadece Google'da arama yapmıyor; yapay zekâlara doğrudan soru soruyor.
          </p>

          <ExampleQuestions />

          <p className="text-slate-300 leading-relaxed mt-6 mb-12">
            Bu soruların cevaplarında görünür olmak, yeni dijital rekabet alanıdır.
            <strong className="text-white"> AI Visibility tam olarak bu noktada devreye girer.</strong>
          </p>

          {/* Article sections */}
          <Article>
            <Section icon={<TrendingUp size={18} />} title="AI Visibility Neden Önemlidir?">
              <p>
                İnsanların bilgi arama alışkanlığı değişiyor. Kullanıcılar artık uzun uzun arama
                sonuçlarını gezmek yerine, yapay zekâlardan doğrudan öneri almak istiyor.
              </p>
              <p>
                Bu durum işletmeler için yeni bir gerçek oluşturuyor: Google'da var olmak artık
                tek başına yeterli değil. İşletmenizin yapay zekâlar tarafından da anlaşılması,
                güvenilir bulunması ve önerilebilir hale gelmesi gerekiyor.
              </p>
              <p>
                Bir işletmenin yapay zekâlarda görünür olması; web sitesi, Google Business Profile,
                müşteri yorumları, sosyal medya varlığı, üçüncü parti kaynaklar ve internetteki genel
                bilgi tutarlılığıyla doğrudan ilişkilidir.
              </p>
            </Section>

            <Section icon={<Compass size={18} />} title="AI Visibility Hangi Alanları Kapsar?">
              <p>
                AI Visibility yalnızca web sitesi SEO'sundan ibaret değildir. Daha geniş bir dijital
                görünürlük alanıdır.
              </p>
              <BulletList items={[
                "Web sitenizin yapay zekâlar tarafından anlaşılabilir olması",
                "İşletme bilgilerinizin internette tutarlı görünmesi",
                "Google Business Profile bilgilerinizin doğru ve güncel olması",
                "Müşteri yorumlarınızın kalite ve güven sinyali oluşturması",
                "Markanızın güvenilir kaynaklarda geçmesi",
                "Hizmetlerinizin net şekilde anlatılması",
                "Sektörünüzle ilgili sorulara cevap veren içeriklere sahip olmanız",
                "Yapay zekâların sizi doğru kategoriyle ilişkilendirebilmesi",
              ]} />
              <p>
                Kısacası AI Visibility, işletmenizin dijital dünyada
                <strong className="text-white"> bulunabilir, anlaşılabilir ve önerilebilir </strong>
                hale gelmesidir.
              </p>
            </Section>

            <Section icon={<Search size={18} />} title="SEO ile AI Visibility Arasındaki Fark Nedir?">
              <p>
                <strong className="text-white">SEO</strong>, web sitenizin Google gibi arama
                motorlarında daha üst sıralarda yer almasını hedefler.
              </p>
              <p>
                <strong className="text-white">AI Visibility</strong> ise işletmenizin yapay zekâ
                destekli cevap motorlarında ne kadar görünür olduğunu ve önerilerde yer alma ihtimalini
                artırmayı hedefler.
              </p>
              <div className="not-prose grid sm:grid-cols-2 gap-3 my-5">
                <ComparisonCard
                  badge="SEO"
                  badgeColor="text-slate-300 border-slate-500/30 bg-slate-500/10"
                  title="Google'da üst sıralarda çıkmak"
                  desc="Klasik arama sonuçlarında URL'inizin ilk 10'a girmesini hedefler."
                />
                <ComparisonCard
                  badge="AI Visibility"
                  badgeColor="text-cyan-300 border-cyan-500/30 bg-cyan-500/10"
                  title="Yapay zekâ önerilerinde yer almak"
                  desc="ChatGPT / Gemini cevaplarında işletmenizin adının geçmesini hedefler."
                />
              </div>
              <p>
                SEO hâlâ çok önemlidir. Ancak artık SEO'nun yanında yapay zekâların işletmenizi
                nasıl gördüğünü de ölçmek gerekir.
              </p>
            </Section>

            <Section icon={<Bot size={18} />} title="Yapay Zekâlar İşletmeleri Nasıl Değerlendirir?">
              <p>
                Yapay zekâlar bir işletmeyi önerirken tek bir kaynağa bakmaz. İnternetteki farklı
                sinyalleri değerlendirir. Örneğin bir restoran için şu bilgiler önemli olabilir:
              </p>
              <BulletList items={[
                "Web sitesinde menü, konum ve iletişim bilgilerinin açık olması",
                "Google yorumlarının güçlü olması",
                "Farklı platformlarda işletme bilgilerinin tutarlı olması",
                "Sosyal medyada aktif ve gerçek görünmesi",
                "Kullanıcıların işletme hakkında olumlu içerikler üretmesi",
                "İşletmenin hangi konuda iyi olduğunun net anlaşılması",
              ]} />
              <p>
                Bir klinik, otel, güzellik merkezi, kamp alanı veya hizmet firması için de benzer
                şekilde farklı dijital sinyaller önem kazanır.
              </p>
            </Section>

            <Section icon={<Target size={18} />} title="uSeen Bu Konuda Ne Yapar?">
              <p>
                <strong className="text-white">uSeen</strong>, işletmenizin yapay zekâlar tarafından
                ne kadar görünür, anlaşılır ve önerilebilir olduğunu analiz eder. Sadece rapor vermez;
                eksikleri uygulanabilir görevlere dönüştürür.
              </p>
              <p>uSeen ile şunları görebilirsiniz:</p>
              <BulletList items={[
                "İşletmeniz yapay zekâlarda ne kadar görünür?",
                "Rakipleriniz sizden daha mı görünür?",
                "Hangi bilgileriniz eksik veya tutarsız?",
                "Web siteniz yapay zekâlar tarafından doğru anlaşılıyor mu?",
                "Hangi müşteri niyetlerinde güçlü, hangilerinde zayıfsınız?",
                "Hangi içerikler üretilirse görünürlük artabilir?",
                "Hangi aksiyonlar öncelikli olarak yapılmalı?",
              ]} />
              <p>
                uSeen, işletmenizin dijital varlığını analiz eder, eksikleri belirler ve yapay
                zekâlarda daha görünür olmanız için uygulanabilir bir yol haritası sunar.
              </p>
            </Section>

            <Section icon={<CheckCircle2 size={18} />} title="Kimler İçin Uygundur?">
              <p>
                AI Visibility özellikle yerel ve hizmet odaklı işletmeler için kritik hale gelmektedir.
                uSeen şu işletmeler için uygundur:
              </p>
              <div className="not-prose grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
                {[
                  "Restoranlar", "Kafeler", "Klinikler",
                  "Güzellik merkezleri", "Oteller", "Kamp alanları",
                  "Oto servisleri", "Emlak ofisleri", "Özel okullar",
                  "Sağlık & bakım", "Yerel hizmet", "Diğer markalar",
                ].map((label) => (
                  <div key={label} className="rounded-lg border border-white/5 bg-black/30 px-3 py-2 text-sm text-slate-300">
                    {label}
                  </div>
                ))}
              </div>
              <p>
                Kullanıcılar yapay zekâlardan öneri almaya başladıkça, bu alan
                <strong className="text-white"> her sektör için daha önemli </strong>
                hale gelecektir.
              </p>
            </Section>

            <Section title="Sonuç">
              <p>
                <strong className="text-white">AI Visibility, dijital dünyanın yeni görünürlük
                alanıdır.</strong>
              </p>
              <p>
                Bugünün arama motorları artık sadece klasik arama sonuçlarından ibaret değil.
                Kullanıcılar cevapları, önerileri ve karşılaştırmaları doğrudan yapay zekâlardan
                almaya başlıyor.
              </p>
              <p>Bu yüzden işletmeler için yeni soru şudur:</p>
              <div className="not-prose my-5 space-y-2 text-base">
                <div className="rounded-xl border border-slate-500/20 bg-slate-500/5 px-4 py-3 text-slate-400 line-through decoration-slate-600">
                  "Google'da var mıyım?"
                </div>
                <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-3 text-white">
                  "Yapay zekâlar beni anlıyor, güvenilir buluyor ve önerilere hazır görüyor mu?"
                </div>
              </div>
              <p>
                uSeen, bu sorunun cevabını ölçmenize ve görünürlüğünüzü artırmanıza yardımcı olur.
              </p>
            </Section>
          </Article>

          {/* CTA */}
          <div
            className="mt-12 rounded-3xl border border-cyan-500/20 bg-cyan-500/5 p-7 sm:p-10 relative overflow-hidden"
            data-testid="ai-visibility-cta-block"
          >
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-cyan-500/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                AI görünürlüğünüzü ölçmeye başlayın
              </h3>
              <p className="text-slate-300 leading-relaxed max-w-2xl mb-6">
                İşletmenizin yapay zekâ aramalarında hangi müşteri niyetlerinde güçlü, hangi
                alanlarda eksik olduğunu görmek için ücretsiz kontrol ile başlayın.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/kayit"
                  className="neon-btn rounded-xl px-5 py-3 text-white font-semibold inline-flex items-center gap-2"
                  data-testid="ai-visibility-cta-primary"
                >
                  Ücretsiz Başla <ArrowRight size={14} />
                </Link>
                <Link
                  to="/fiyatlandirma"
                  className="ghost-btn rounded-xl px-5 py-3 text-white border border-white/10 inline-flex items-center gap-2"
                  data-testid="ai-visibility-cta-secondary"
                >
                  Planları Gör
                </Link>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <section className="mt-16" data-testid="ai-visibility-faq">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-3">
              <HelpCircle size={11} /> Sık Sorulan Sorular
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-6">
              AI Visibility hakkında merak edilenler
            </h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-2xl border border-white/5 bg-black/30 px-5 py-4 open:bg-white/[0.03] transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-white font-medium">
                    <span>{f.q}</span>
                    <span className="text-cyan-300 text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-slate-300 leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-sm text-slate-400 text-center">
              Sorularınız mı var?{" "}
              <Link to="/iletisim" className="text-cyan-300 hover:text-cyan-200" data-testid="ai-visibility-contact-link">
                Bizimle iletişime geçin
              </Link>
              .
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function Article({ children }) {
  return <article className="space-y-10 text-slate-300 leading-relaxed">{children}</article>;
}

function Section({ icon, title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl sm:text-[1.7rem] font-bold text-white mb-3 flex items-center gap-2.5">
        {icon && <span className="text-cyan-300">{icon}</span>}
        {title}
      </h2>
      <div className="space-y-3 text-[0.975rem]">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-1.5 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ComparisonCard({ badge, badgeColor, title, desc }) {
  return (
    <div className="rounded-xl border border-white/5 bg-black/30 p-4">
      <div className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${badgeColor} mb-2`}>
        {badge}
      </div>
      <div className="text-white text-sm font-medium mb-1">{title}</div>
      <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function ExampleQuestions() {
  const examples = [
    "Bana yakınımdaki iyi bir restoranı öner.",
    "İstanbul'da güvenilir bir güzellik merkezi var mı?",
    "Bu bölgede en iyi kamp alanı hangisi?",
    "Bu hizmeti veren güvenilir firmalar kimler?",
  ];
  return (
    <div className="rounded-2xl border border-white/5 bg-black/30 p-5">
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-3 flex items-center gap-1.5">
        <Bot size={11} /> Yapay zekâya sorulan örnek sorular
      </div>
      <ul className="space-y-2">
        {examples.map((q, i) => (
          <li key={i} className="text-slate-300 text-[0.95rem] flex items-start gap-2.5">
            <span className="text-cyan-400 shrink-0">›</span>
            <span>"{q}"</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
